import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";

// Models
import {
  IProductGroupFilterFormControls,
  IProductGroupFilterParams,
} from "../../interfaces/product-group.interface";

/**
 * Product Groups Filters Component
 * Simple filter with search by Name or ID
 */
@Component({
  selector: "app-product-groups-filters",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
  ],
  templateUrl: "./product-groups-filters.component.html",
  styleUrl: "./product-groups-filters.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductGroupsFiltersComponent implements OnInit, OnDestroy {
  @Input() filterForm!: FormGroup<IProductGroupFilterFormControls>;
  @Input() loading = false;
  @Input() drawerMode = true;

  @Output() filtersChanged = new EventEmitter<IProductGroupFilterParams>();
  @Output() clearFilters = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();

  /**
   * Check if any filters are active
   */
  get hasActiveFilters(): boolean {
    if (!this.filterForm) return false;
    return !!this.filterForm.value.search?.trim();
  }

  ngOnInit(): void {
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup form subscriptions for real-time filtering
   */
  private setupFormSubscriptions(): void {
    if (!this.filterForm) return;

    // Debounce search input
    this.filterForm.controls.search.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitFilterChange();
      });
  }

  /**
   * Emit filter changes to parent component
   */
  private emitFilterChange(): void {
    if (!this.filterForm) return;

    const filter: IProductGroupFilterParams = {
      search: this.filterForm.value.search?.trim() || undefined,
    };

    this.filtersChanged.emit(filter);
  }

  /**
   * Clear all filters
   */
  onClearFilters(): void {
    if (!this.filterForm) return;

    // Let valueChanges fire normally so distinctUntilChanged sees the "" value
    // The parent component handles duplicate prevention via skipNextFilterChange flag
    this.filterForm.patchValue({
      search: "",
    });

    this.clearFilters.emit();
  }
}
